// =============================================================================
// investmentTransferRules.js — JS mirror of lib/investment_transfer_rules.py
//
// Belt-and-suspenders defence for the Monthly Spending Trend chart. Even if a
// legacy transaction was saved with `type='expense'` and category 'Investments',
// this helper prevents it from being counted as spending on the client side.
// The canonical source of truth remains the backend Python module — keep this
// file in sync when patterns change there.
//
// Case sensitivity nuance: Python supports `(?-i:...)` for per-pattern case
// sensitivity. JS does not, so we simulate it by splitting the pattern list
// into two arrays: CI (case-insensitive) and CS (case-sensitive). The
// exported `isInvestmentTransfer` runs both.
// =============================================================================

const _BROKER_TOKENS =
  '(?:VANGUARD|FIDELITY|SCHWAB|ROBINHOOD|MERRILL|ETRADE|E\\*TRADE|'
  + 'ISHARES|SPDR|INVESCO|BLACKROCK|VONG|VGT|VDE|VTI|VOO|VXUS|VYM|VUG|VNQ|VWO|'
  + 'IVOG|IWM|QQQ|SPY|BND|AGG|SCHB|SCHF|SCHZ|FZROX|FZILX|FSKAX|FTIHX)';

// Case-insensitive patterns
const CI_PATTERNS = [
  // US brokers
  /\bVANGUARD\s*BUY\b/i,
  /\bSCHWAB\s*BUY\b/i,
  /\bFIDELITY\s*BUY\b/i,
  /\bROBINHOOD\s*BUY\b/i,
  /\bE\*?TRADE\s*BUY\b/i,
  /\bMERRILL\s*BUY\b/i,
  /\bVMC\s*PUR\b/i,
  new RegExp(`\\bBuy\\b.{0,40}\\b${_BROKER_TOKENS}\\b`, 'i'),
  new RegExp(`\\b${_BROKER_TOKENS}\\b.{0,40}\\bBuy\\b`, 'i'),
  /^\s*Buy\s+[A-Z]{2,6}\s*$/,
  /\bREINVESTMENT\b/i,
  /\bDIVIDEND\s+REINVESTMENT\b/i,
  /\bDIV\s+REINV\b/i,
  /\bTRANSFER\s+TO\s+BROKERAGE\b/i,
  /\bTRANSFER\s+TO\s+INVESTMENT\b/i,
  /\bTRANSFER\s+TO\s+VANGUARD\b/i,
  /\bTRANSFER\s+TO\s+FIDELITY\b/i,
  /\bTRANSFER\s+TO\s+SCHWAB\b/i,
  // Vanguard's phrasing for external bank funding INTO the brokerage.
  /\bFunds\s+received\s+via\s+Electronic\s+Bank\s+Transfer\b/i,
  /\bElectronic\s+Bank\s+Transfer\b/i,
  // India own-account transfers + ATM cash withdrawals (fire on outflows).
  /\bOWN\s+ACC(?:OUNT)?\b/i,
  /\bOWN\s+A\/C\b/i,
  /\bTO\s+SELF\b/i,
  /\bSELF\s+TRANSFER\b/i,
  /\bOWN\s+ACCO\b/i,
  /\bWDL\s+TFR\b/i,
  /\bATW\b/i,
  /\bATM\s+(?:WITHDRAWAL|WDL)\b/i,
  /\bATM\s+CASH\s+WDL\b/i,
  /\bCASH\s+WITHDRAWAL\b/i,
  /\bCASH\s+WDL\b/i,
  /\bNWD\b/i,
  /\bCDM\b/i,
  // SELL — cash-leg proceeds of position liquidations. Word-anchored to
  // avoid retail names like "Sellers restaurant" or "Sell out at Comedy Club".
  /^\s*Sell\s*$/,
  /^\s*Sell\s+[A-Z]{2,6}\s*$/,
  /\b(?:VANGUARD|FIDELITY|SCHWAB|MERRILL|ROBINHOOD|ETRADE|E\*TRADE|MORGAN\s+STANLEY|TD\s+AMERITRADE)\s+SELL\b/i,
  /\bSELL\s+[A-Z]{2,6}\s+SHARES?\b/i,
  /\bREDEEMED\s+SHARES?\b/i,
  /\bSHARE\s+REDEMPTION\b/i,
  // Broker settlement-fund sweeps — internal cash <-> cash inside brokerage.
  /\bSWEEP\s+(?:INTO|OUT\s+OF)\s+SETTLEMENT(?:\s+FUND)?\b/i,
  /\bSWEEP\s+(?:IN|OUT)\b/i,
  /\bCASH\s+MANAGEMENT\s+SWEEP\b/i,
  /\bSETTLEMENT\s+FUND\b/i,
  // India brokers
  /\bGROWW\b/i,
  /\bZERODHA\b/i,
  /\bUPSTOX\b/i,
  /\bICCL\b/i,
  /\bBSE\s+STAR\s*MF\b/i,
  /\bNSE\s+MFSS\b/i,
  /\bKUVERA\b/i,
  /\bETMONEY\b/i,
  /\bCOIN\s+BY\s+ZERODHA\b/i,
  /\bSMALLCASE\b/i,
  /\bPAYTM\s+MONEY\b/i,
  /\bANGEL\s+ONE\b/i,
  /\bICICI\s+DIRECT\b/i,
  /\bHDFC\s+SEC(URITIES)?\b/i,
  /\bKOTAK\s+SEC(URITIES)?\b/i,
  /\bMOTILAL\s+OSWAL\b/i,
  /\b5PAISA\b/i,
  /\bSHAREKHAN\b/i,
  // Mutual funds / retirement
  /\bMUTUAL\s+FUND\b/i,
  /\bMF\s+PURCHASE\b/i,
  /\bNPS\s+CONTRIBUTION\b/i,
  /\bPPF\s+DEPOSIT\b/i,
  /\bELSS\b/i,
  /\bNFO\b/i,
  // UK / EU brokers
  /\bHARGREAVES\s+LANSDOWN\b/i,
  /\bAJ\s*BELL\b/i,
  /\bINTERACTIVE\s+INVESTOR\b/i,
  /\bTRADING212\b/i,
  /\bDEGIRO\b/i,
  // APAC / ME brokers
  /\bSTAKE\b/i,
  /\bCOMMSEC\b/i,
  /\bSAXO\b/i,
  /\bSARWA\b/i,
  /\bDBS\s+VICKERS\b/i,
  /\bFSMONE\b/i,
];

// Case-sensitive patterns (SIP is a common English verb in lowercase — we
// only want to match the ALL-CAPS bank-abbreviation form).
const CS_PATTERNS = [
  /\bSIP\b/,
  /\bSIP\s*INSTALL(?:MENT)?\b/,
  /\bSIP\s*AMOUNT\b/,
  /\bSIP\s*[-_]/,
];

export const INVESTMENT_TRANSFER_PATTERNS = [...CI_PATTERNS, ...CS_PATTERNS];

/**
 * Return true if ANY of the passed text fields matches an investment-transfer
 * pattern. Callers pass (description, merchant); empty/null values are ignored.
 */
export function isInvestmentTransfer(...fields) {
  for (const f of fields) {
    if (!f) continue;
    const s = String(f);
    for (const rx of CI_PATTERNS) if (rx.test(s)) return true;
    for (const rx of CS_PATTERNS) if (rx.test(s)) return true;
  }
  return false;
}
